import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError as observableThrowError } from 'rxjs';
import { catchError, map, publishReplay, refCount, switchMap } from 'rxjs/operators';
import { DeployParams } from 'src/app/deployParams';
import { ICurrencyTO } from 'src/app/models/settings/transportObjects/currencyTO.interface';
import { ISuperBetSettingsTO } from 'src/app/models/settings/transportObjects/superBetSettingsTO.interface';
import { IBetTypesShowTypesTO } from 'src/app/models/betType/transportObjects/betTypeShowTypesTO.interface';
import { BaseService } from './base.service';
import { IBusinessModelTO } from 'src/app/models/settings/transportObjects/businessModelTO.interface';
import { ModelMapperService } from '../util/model.mapper.service';
import { IMultiwayRestrictionTO } from 'src/app/models/settings/transportObjects/multiwayRestrictionTO.interface';
import { IBonusSettingsTO } from 'src/app/models/settings/transportObjects/bonusSettingsTO.interface';
import { IFreeBetSettingTO } from 'src/app/models/settings/transportObjects/freeBetSettingTO.interface';
import { NotificationService } from '../notification.service';
import { LoaderService } from '../loader.service';
import { UserActivityService } from '../userActivity.service';
import { StorageService } from '../util/storage.service';
import { IOutcomeCodeValueTO } from 'src/app/models/betType/transportObjects/outcomeCodeValueTO.interface';
import { ICurrencyWithSecondaryCurrencyTO } from 'src/app/models/settings/transportObjects/currencyWithSecondaryCurrencyTO.interface';
import { environment } from 'src/environments/environment';

@Injectable()
export class SettingsProviderService extends BaseService {
  languageForBetTypeShowTypes: string = null;
  betTypeShowTypesObservable: Observable<IBetTypesShowTypesTO> = null;
  outcomeCodeValuesObservable: Observable<{outcomeCodeValues: IOutcomeCodeValueTO[]}> = null;
  constructor(private http: HttpClient,
              private modelMapper: ModelMapperService,
              protected loaderService: LoaderService,
              protected userActivityService: UserActivityService,
              protected notificationService: NotificationService,
              protected storage: StorageService) {
    super(loaderService, userActivityService, notificationService, storage);
  }


  getBussinesModel(): Observable<IBusinessModelTO> {
    const params = new HttpParams();
    const url: string = this.slowCacheController + 'businessModel/' + environment.betWebOfficeId;

    return this.http.get(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IBusinessModelTO),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getBussinesModelTicketManager(): Observable<IBusinessModelTO> {
    const params = new HttpParams();
    const url: string = this.baseUrlTicketManager + 'businessModel/';

    return this.http.get(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IBusinessModelTO),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getCurrency(): Observable<ICurrencyWithSecondaryCurrencyTO> {
    const params = new HttpParams();
    const url: string = this.slowCacheController + 'Currency';

    return this.http.get(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ICurrencyTO),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getSuperBetSetting(officeId: number): Observable<ISuperBetSettingsTO> {
    const params = new HttpParams();
    const url: string = this.slowCacheController + 'superBetSetting/' + officeId;

    return this.http.get(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ISuperBetSettingsTO),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  // make interface for translations???
  getDynamicTranslations(languageCulture: string): Observable<any> {
    const params = new HttpParams();
    const url: string = 'locale/dynamicTranslation.' + languageCulture + '.txt?v=' + environment.productVersion.toString();

    return this.http.get(url).pipe(
      map((response) => response as any),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err, false));
        return [];
      }),
    );
  }

  getVersion(): Observable<any> {
    const params = new HttpParams();
    const url: string = 'version.txt?v=' + (new Date()).toString();

    return this.http.get(url).pipe(
      map((response) => response as any),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err, false));
        return [];
      }),
    );
  }

  acceptanceTimeout(): Observable<{ acceptanceTimeoutPM: number; acceptanceTimeoutLive: number }> {
    const params = new HttpParams();
    const url: string = this.slowCacheController + 'acceptanceTimeout';

    return this.http.get<{ acceptanceTimeoutPM: number; acceptanceTimeoutLive: number }>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as { acceptanceTimeoutPM: number; acceptanceTimeoutLive: number }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
        // if (err.status == 401) {
        //     this.showUnauthorize();
        // } else {
        //     this.showErrorMessage();
        // }
        // return Observable.throw(this.handleError(err));
      }),
    );
  }

  getTBetTypeShowTypes(language: string): Observable<IBetTypesShowTypesTO> {
    const params = new HttpParams();
    const url: string = this.slowCacheController + 'TBetTypeShowTypes';
    if (this.languageForBetTypeShowTypes !== language || !this.betTypeShowTypesObservable) {
      this.languageForBetTypeShowTypes = language;
      this.betTypeShowTypesObservable = this.http.get(url, { headers: this.httpHeaders, params }).pipe(
        switchMap((response) => {
          this.betTypeShowTypesObservable = null;
          return of(response as IBetTypesShowTypesTO);
        }),
        publishReplay(1),
        refCount(),
        catchError((err) => {
          observableThrowError(this.handleError(err));
          return [];
        }),
      );
    }
    return this.betTypeShowTypesObservable;
  }

  getMultiwayRestrictions(): Observable<{ [betTypeOutcomeId: number]: IMultiwayRestrictionTO }> {
    const url: string = this.slowCacheController + 'GetMultiwayRestrictions';
    const params = new HttpParams();
    return this.http.get(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as { [betTypeOutcomeId: number]: IMultiwayRestrictionTO }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }


  getBonusSettings(officeId: number): Observable<IBonusSettingsTO[]> {
    const url: string = this.slowCacheController + 'bonus/' + officeId;
    const params = new HttpParams();
    return this.http.get(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IBonusSettingsTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getBonusSettingsForPlayer(bidderId: number, isAffiliate: boolean): Observable<IBonusSettingsTO[]> {
    if (isAffiliate) {
      return this.getBonusSettingsForAffiliate(bidderId);
    }
    const url: string = this.baseUrlWebClientController + 'BonusForPlayer/' + bidderId;
    const params = new HttpParams();
    return this.http.get(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IBonusSettingsTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) =>
        // observableThrowError(this.handleError(err));
        of([])
      ),
    );
  }

  getBonusSettingsForAffiliate(bidderId: number): Observable<IBonusSettingsTO[]> {
    const url: string = this.baseUrlWebClientAffiliate + 'BonusForPlayer/' + bidderId;
    const params = new HttpParams();
    return this.http.get(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IBonusSettingsTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) =>
        // observableThrowError(this.handleError(err));
        of ([])
      ),
    );
  }

  getFreeBetSettings(bidderId: number): Observable<IFreeBetSettingTO> {
    const url: string = this.baseUrlWebClientController + 'FreeBetSetting/' + bidderId;
    const params = new HttpParams();
    return this.http.get(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IBonusSettingsTO),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getOutcomeCodeValues(): Observable<{ outcomeCodeValues: IOutcomeCodeValueTO[] }> {
    const url: string = this.sportBookOfferController + 'GetOutcomeCodeValues';
    const params = new HttpParams();
    if (!this.outcomeCodeValuesObservable) {
      this.outcomeCodeValuesObservable = this.http.get(url, { headers: this.httpHeaders, params }).pipe(
        map((response) => response as { outcomeCodeValues: IOutcomeCodeValueTO[] }),
        publishReplay(1),
        refCount(),
        catchError((err) => {
          observableThrowError(this.handleError(err));
          return [];
        }),
      );
    }
    return this.outcomeCodeValuesObservable;
  }
}
